/**
 * Ingredient Marquee — adaptive CSS loop, detail dialog and pause control.
 *
 * The browser animates one transform. JavaScript only creates the minimum number of copies needed
 * to fill the rail, recalculates after a width change, opens the native dialog and toggles pause.
 */
(function () {
  function copiesForWidth(containerWidth, sequenceWidth) {
    if (containerWidth <= 0 || sequenceWidth <= 0) return 1;
    return Math.max(1, Math.ceil(containerWidth / sequenceWidth));
  }

  if (window.__rootsIngredientMarqueeInit) return;
  window.__rootsIngredientMarqueeInit = true;

  var controllers = new Map();

  function teardownRoot(rootId) {
    var controller = controllers.get(rootId);
    if (controller) {
      controller.abort();
      controllers.delete(rootId);
    }
  }

  function cloneItem(item) {
    var clone = item.cloneNode(true);
    clone.removeAttribute('data-original-item');
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('data-marquee-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[data-tile]').forEach(function (tile) {
      tile.tabIndex = -1;
    });
    return clone;
  }

  function initRoot(root) {
    if (!root) return;
    teardownRoot(root.id);

    var controller = new AbortController();
    var signal = controller.signal;
    controllers.set(root.id, controller);

    var dialog = root.querySelector('[data-dialog]');
    var dialogMedia = root.querySelector('[data-dialog-media]');
    var dialogTitle = root.querySelector('[data-dialog-title]');
    var dialogBody = root.querySelector('[data-dialog-body]');
    var rail = root.querySelector('[data-rail]');
    var track = root.querySelector('[data-track]');
    var toggle = root.querySelector('[data-toggle]');
    var resizeTimer;
    var lastRailWidth = -1;

    function closeDialog() {
      if (dialog && dialog.open) dialog.close();
    }

    function openDialog(tile) {
      var item = tile.closest('.roots-im__item');
      if (!dialog || !item) return;

      var detail = item.querySelector('[data-detail]');
      if (!detail) return;

      var nameEl = detail.querySelector('[data-detail-name]');
      if (dialogTitle) dialogTitle.textContent = nameEl ? nameEl.textContent.trim() : '';

      if (dialogMedia) {
        dialogMedia.replaceChildren();
        var img = tile.querySelector('.roots-im__tile-image');
        if (img) {
          var clone = img.cloneNode(true);
          clone.removeAttribute('loading');
          clone.removeAttribute('sizes');
          clone.alt = '';
          dialogMedia.appendChild(clone);
        }
      }

      if (dialogBody) dialogBody.replaceChildren.apply(dialogBody, detail.cloneNode(true).childNodes);
      dialog.showModal();
    }

    function rebuildTrack() {
      if (!rail || !track) return;

      track.removeAttribute('data-loop-ready');
      track.querySelectorAll('[data-marquee-clone]').forEach(function (clone) {
        clone.remove();
      });

      var originalItems = Array.from(track.querySelectorAll('[data-original-item]'));
      if (!originalItems.length) return;

      var sequenceWidth = track.scrollWidth;
      var repeatCount = copiesForWidth(rail.clientWidth, sequenceWidth);

      for (var copy = 1; copy < repeatCount; copy += 1) {
        originalItems.forEach(function (item) {
          track.appendChild(cloneItem(item));
        });
      }

      var firstHalf = Array.from(track.children);
      var halfWidth = track.scrollWidth;
      firstHalf.forEach(function (item) {
        track.appendChild(cloneItem(item));
      });

      var speed = Number(rail.dataset.speedPx) || 40;
      track.style.setProperty('--roots-im-duration', halfWidth / speed + 's');
      track.setAttribute('data-loop-ready', 'true');
      lastRailWidth = rail.clientWidth;
    }

    function scheduleRebuild() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(rebuildTrack, 150);
    }

    if (rail && track) {
      rebuildTrack();

      if ('ResizeObserver' in window) {
        var observer = new ResizeObserver(function (entries) {
          var width = entries[0] ? entries[0].contentRect.width : rail.clientWidth;
          if (Math.round(width) !== Math.round(lastRailWidth)) scheduleRebuild();
        });
        observer.observe(rail);
        signal.addEventListener(
          'abort',
          function () {
            window.clearTimeout(resizeTimer);
            observer.disconnect();
          },
          { once: true }
        );
      } else {
        window.addEventListener('resize', scheduleRebuild, { signal: signal });
      }
    }

    if (dialog) {
      // Suppress focus on a generated tile without suppressing activation. This MUST stay on
      // mousedown: WebKit ties the synthesized click to the cancelled pointer-down, so cancelling
      // pointerdown here leaves every generated tile dead to touch in Safari (Chromium, which
      // follows Pointer Events L3 and fires click regardless, hides the bug).
      root.addEventListener(
        'mousedown',
        function (event) {
          var tile = event.target.closest('[data-marquee-clone] [data-tile]');
          if (tile && root.contains(tile)) event.preventDefault();
        },
        { signal: signal }
      );

      root.addEventListener(
        'click',
        function (event) {
          var tile = event.target.closest('[data-tile]');
          if (tile && root.contains(tile)) openDialog(tile);
        },
        { signal: signal }
      );

      var closeBtn = root.querySelector('[data-dialog-close]');
      if (closeBtn) closeBtn.addEventListener('click', closeDialog, { signal: signal });

      dialog.addEventListener(
        'click',
        function (event) {
          if (event.target === dialog) closeDialog();
        },
        { signal: signal }
      );

      dialog.addEventListener(
        'close',
        function () {
          if (dialogBody) dialogBody.replaceChildren();
          if (dialogMedia) dialogMedia.replaceChildren();
        },
        { signal: signal }
      );
    }

    if (toggle && rail) {
      toggle.addEventListener(
        'click',
        function () {
          var playing = toggle.dataset.playing !== 'false';
          var next = !playing;
          toggle.dataset.playing = String(next);
          rail.dataset.paused = String(!next);
          toggle.setAttribute(
            'aria-label',
            next ? 'Pause the ingredient carousel' : 'Play the ingredient carousel'
          );
        },
        { signal: signal }
      );
    }

    // Plain tiles hold nothing focusable, so under reduced motion -- the one state where the rail
    // scrolls (overflow-x: auto) -- the rail itself takes the tab stop. WebKit does not make a
    // scroll container keyboard-focusable on its own; Chromium does. Never in motion mode: the rail
    // is overflow: hidden there, and a click- or tap-focused rail would hold the :focus-within pause.
    if (rail && root.classList.contains('roots-im--plain')) {
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      var syncRailFocus = function () {
        if (reducedMotion.matches) rail.setAttribute('tabindex', '0');
        else rail.removeAttribute('tabindex');
      };
      syncRailFocus();
      reducedMotion.addEventListener('change', syncRailFocus, { signal: signal });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-roots-im]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = document.getElementById('roots-ingredient-marquee-' + event.detail.sectionId);
    if (root) initRoot(root);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    teardownRoot('roots-ingredient-marquee-' + event.detail.sectionId);
  });
})();
